import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';

import { AppError } from '@/lib/error';
import { rollbar } from '@/lib/rollbar';

import {
  AuthAdapter,
  AuthCapabilities,
  AuthFlowType,
  AuthState,
  AuthToken,
  AuthUser,
  CredentialsInput,
} from './auth-adapter';

export type OIDCAdapterConfig = {
  /** The OIDC authority URL (issuer) */
  authority: string;
  /** The client ID registered with the OIDC provider */
  clientId: string;
  /** The redirect URI after successful authentication */
  redirectUri: string;
  /** The redirect URI after sign out (optional) */
  postLogoutRedirectUri?: string;
  /** OAuth scopes to request (defaults to 'openid profile email') */
  scopes?: string[];
  /** Enable silent token renewal (defaults to true) */
  automaticSilentRenew?: boolean;
  /** Silent renewal iframe timeout in milliseconds (defaults to 10000) */
  silentRequestTimeoutInSeconds?: number;
};

function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return {};
  }
}

function mapOIDCUser(user: User): AuthUser {
  const profile = user.profile;
  const accessTokenPayload = decodeJwtPayload(user.access_token);
  const accessTokenRole = accessTokenPayload['role'] as string | undefined;
  const profileRole = profile['role'] as string | undefined;

  return {
    id: profile.sub,
    email: profile.email,
    name: profile.name || profile.preferred_username,
    avatar: profile.picture,
    role: accessTokenRole || profileRole,
    permissions: (profile['permissions'] as string[] | undefined) || [],
    metadata: {
      emailVerified: profile.email_verified,
      locale: profile.locale,
      ...profile,
    },
  };
}

function mapOIDCToken(user: User): AuthToken {
  return {
    accessToken: user.access_token,
    refreshToken: user.refresh_token,
    idToken: user.id_token,
    expiresAt: user.expires_at ? new Date(user.expires_at * 1000) : undefined,
    tokenType: user.token_type || 'Bearer',
  };
}

function createAuthState(user: User | null, error: AppError | null = null): AuthState {
  if (error) {
    return {
      user: null,
      token: null,
      status: 'error',
      error,
    };
  }

  if (!user || user.expired) {
    return {
      user: null,
      token: null,
      status: 'unauthenticated',
      error: null,
    };
  }

  return {
    user: mapOIDCUser(user),
    token: mapOIDCToken(user),
    status: 'authenticated',
    error: null,
  };
}

// localStorage keys for cross-tab refresh coordination
const STORAGE_KEY_REFRESHING = 'auth:token:refreshing';
const STORAGE_KEY_RESULT = 'auth:token:result';

type RefreshResult = { token: string | null; ts: number; error?: string };

export class OIDCAdapter extends AuthAdapter {
  readonly name = 'Task Masters';
  readonly flowType: AuthFlowType = 'redirect';
  readonly capabilities: AuthCapabilities = {
    canSignInWithCredentials: false,
    canSignUp: false,
    canResetPassword: false,
    canChangePassword: false,
  };

  private refreshing = false;
  private pendingTokens: Array<{
    resolve: (token: string | null) => void;
    reject: (error: unknown) => void;
  }> = [];
  private userManager: UserManager;
  private currentState: AuthState;
  private listeners: Set<(state: AuthState) => void> = new Set();

  constructor(config: OIDCAdapterConfig) {
    super();

    const scopes = config.scopes?.join(' ') || 'openid profile email offline_access';

    this.userManager = new UserManager({
      authority: config.authority,
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      post_logout_redirect_uri: config.postLogoutRedirectUri || config.redirectUri,
      scope: scopes,
      response_type: 'code',
      automaticSilentRenew: config.automaticSilentRenew ?? true,
      silentRequestTimeoutInSeconds: config.silentRequestTimeoutInSeconds ?? 3,
      // Not ideal, but since we use rotating refresh tokens, each refresh is valid
      // for a short period of time, so storing it in the local storage is less of
      // a problem. See: https://auth0.com/blog/refresh-tokens-what-are-they-and-when-to-use-them/#You-Can-Store-Refresh-Token-In-Local-Storage
      userStore:
        typeof window !== 'undefined'
          ? new WebStorageStateStore({ store: window.localStorage })
          : undefined,
    });

    this.currentState = {
      user: null,
      token: null,
      status: 'idle',
      error: null,
    };

    this.setupEventListeners();
    this.setupStorageListener();

    // Make sure to bind the methods to avoid problem when called from outside the class
    this.initialize = this.initialize.bind(this);
    this.getAuthState = this.getAuthState.bind(this);
    this.getAccessToken = this.getAccessToken.bind(this);
    this.signIn = this.signIn.bind(this);
    this.signUp = this.signUp.bind(this);
    this.signOut = this.signOut.bind(this);
    this.resetPassword = this.resetPassword.bind(this);
    this.changePassword = this.changePassword.bind(this);
    this.getAccessToken = this.getAccessToken.bind(this);
  }

  /**
   * Listens for cross-tab refresh signals. The storage event only fires in tabs
   * that did NOT make the change, so this is purely reactive — no polling.
   *
   * - STORAGE_KEY_REFRESHING set → another tab is refreshing; mark this tab as
   *   refreshing so callers queue instead of racing to do their own refresh.
   * - STORAGE_KEY_RESULT set → another tab finished; flush our pending queue with
   *   the broadcast token and sync state from storage.
   */
  private setupStorageListener(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('storage', async (event) => {
      if (event.key === STORAGE_KEY_REFRESHING && event.newValue === '1') {
        console.warn('cross-tab: another tab started token refresh, marking as refreshing');
        this.refreshing = true;
        return;
      }

      if (event.key === STORAGE_KEY_RESULT && event.newValue) {
        try {
          const result = JSON.parse(event.newValue) as RefreshResult;
          console.warn('cross-tab: received token refresh result from another tab');

          this.refreshing = false;

          if (result.error) {
            this.updateState(createAuthState(null));
            this.flushPendingTokens(null, new Error(result.error));
          } else {
            // Unblock queued callers immediately; state sync is async
            this.flushPendingTokens(result.token);
            const user = await this.userManager.getUser();
            if (user && !user.expired) this.updateState(createAuthState(user));
          }
        } catch (error) {
          rollbar.error('failed to update token from another tab', error as Error, { event });
        }
      }
    });
  }

  private setupEventListeners(): void {
    // User loaded (after sign in or silent renew)
    this.userManager.events.addUserLoaded((user) => {
      console.warn(
        'user loaded, token expires at:',
        user.expires_at ? new Date(user.expires_at * 1000) : 'unknown',
      );
      this.updateState(createAuthState(user));
    });

    // User unloaded (after sign out)
    this.userManager.events.addUserUnloaded(() => {
      console.warn('user unloaded, clearing auth state');
      this.updateState(createAuthState(null));
    });

    // Silent renew error - user needs to re-authenticate
    this.userManager.events.addSilentRenewError((error) => {
      console.warn('Silent renew failed:', error);
      this.updateState(createAuthState(null));
    });

    // Access token expiring soon — force silent renew immediately
    this.userManager.events.addAccessTokenExpiring(async () => {
      console.warn('access token expiring soon, forcing silent renew');
      await this.performRefresh('expiring-event');
    });

    // Access token expired — only clear state if refresh also fails
    this.userManager.events.addAccessTokenExpired(async () => {
      if (this.refreshing) return;
      console.warn('access token expired, trying to refresh');
      await this.performRefresh('expired-event');
    });
  }

  /**
   * Single source of truth for silent token refresh.
   *
   * If `this.refreshing` is already true — whether set by this tab's own in-flight
   * request or by the cross-tab storage listener — the caller is queued and will
   * receive the result once the active refresh (in any tab) completes.
   *
   * On finish, the result is broadcast via localStorage so sibling tabs can unblock
   * their own queues without running a redundant refresh.
   */
  private async performRefresh(source: string): Promise<string | null> {
    if (this.refreshing) {
      console.warn(`refresh already in progress (${source}), queueing`);
      return new Promise<string | null>((resolve, reject) => {
        this.pendingTokens.push({ resolve, reject });
      });
    }

    this.refreshing = true;
    localStorage.setItem(STORAGE_KEY_REFRESHING, '1');

    try {
      const renewedUser = await this.userManager.signinSilent();
      const token = renewedUser?.access_token ?? null;

      if (!token) {
        throw new AppError('internalError', 'could not get access token from renewed user');
      }

      console.warn(
        `token refreshed (${source}), expires at:`,
        renewedUser?.expires_at ? new Date(renewedUser.expires_at * 1000) : 'unknown',
      );

      this.updateState(createAuthState(renewedUser));
      this.broadcastToken(token);
      this.flushPendingTokens(token);
      return token;
    } catch (error) {
      rollbar.error('Token refresh failed', error as Error);
      const errorMessage = error instanceof Error ? error.message : 'Token refresh failed';

      this.broadcastToken(null, errorMessage);
      this.updateState(createAuthState(null));
      this.flushPendingTokens(null, error);
      return null;
    } finally {
      this.refreshing = false;
      localStorage.removeItem(STORAGE_KEY_REFRESHING);
    }
  }

  private broadcastToken(token: string | null, error?: string): void {
    localStorage.setItem(STORAGE_KEY_RESULT, JSON.stringify({ token, ts: Date.now(), error }));
  }

  private flushPendingTokens(token: string | null, error?: unknown): void {
    const pending = this.pendingTokens.splice(0);
    for (const { resolve, reject } of pending) {
      if (error) reject(error);
      else resolve(token);
    }
  }

  private updateState(newState: AuthState): void {
    this.currentState = newState;
    this.listeners.forEach((listener) => listener(newState));
  }

  async initialize(): Promise<void> {
    try {
      if (this.hasAuthParams()) {
        const user = await this.userManager.signinCallback();
        if (user) {
          console.warn(
            'auth callback complete, token expires at:',
            user.expires_at ? new Date(user.expires_at * 1000) : 'unknown',
          );
          this.updateState(createAuthState(user));
          this.broadcastToken(user.access_token);
          return;
        }
      }

      this.updateState({
        ...this.currentState,
        status: 'loading',
      });

      // Try to get user from storage first
      const user = await this.userManager.getUser();

      if (user && !user.expired) {
        console.warn(
          'user data already available, token expires at:',
          new Date(Date.now() + (user.expires_in || 0) * 1000),
        );
        this.updateState(createAuthState(user));
        this.broadcastToken(user.access_token);
        return;
      }

      // Try silent sign-in if no valid user in storage
      const token = await this.performRefresh('initialize');
      if (!token) {
        console.warn('silent sign-in failed during initialization, user unauthenticated');
      }
    } catch (error) {
      rollbar.error('Failed to initialize OIDC adapter:', error as Error);
      this.updateState(
        createAuthState(
          null,
          error instanceof AppError
            ? error
            : new AppError('internalError', 'Initialization failed', { cause: error }),
        ),
      );
    }
  }

  getAuthState(): AuthState {
    return this.currentState;
  }

  async getAccessToken(): Promise<string | null> {
    try {
      if (this.refreshing) {
        return new Promise<string | null>((resolve, reject) => {
          this.pendingTokens.push({ resolve, reject });
        });
      }

      const user = await this.userManager.getUser();

      if (!user) {
        return null;
      }

      if (user.expired) {
        return this.performRefresh('get-access-token');
      }

      return user.access_token;
    } catch {
      return null;
    }
  }

  protected async signInWithRedirect(): Promise<void> {
    // Task Masters requires consent prompt for offline access
    await this.userManager.signinRedirect({ prompt: 'consent' });
  }

  async signOut(): Promise<void> {
    try {
      await this.userManager.signoutSilent();
    } catch (error) {
      // If silent fails, at least clear local state
      console.error('Sign out redirect failed:', error);
      await this.userManager.removeUser();
      this.updateState(createAuthState(null));
    }
  }

  onAuthStateChange(callback: (state: AuthState) => void): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  protected signInWithCredentials(_credentials: CredentialsInput): Promise<AuthState> {
    throw new Error('Sign-in with credentials is not supported with OIDC adapter');
  }

  signUp(_credentials: CredentialsInput & { name?: string }): Promise<AuthState> {
    throw new Error('Sign-up is not supported by the OIDC adapter');
  }

  resetPassword(_email: string): Promise<void> {
    throw new Error('Password reset is not supported by the OIDC adapter');
  }

  changePassword(_oldPassword: string, _newPassword: string): Promise<void> {
    throw new Error('Password change is not supported by the OIDC adapter.');
  }

  private hasAuthParams(): boolean {
    const searchParams =
      typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;

    if (!searchParams) return false;
    return (
      (!!searchParams.get('code') || !!searchParams.get('error')) && !!searchParams.get('state')
    );
  }
}
